/**
 * Propose a payment run — `/finance/payment-runs`.
 *
 * A proposal changes no invoice, produces no file and posts nothing. It exists
 * to be reviewed, which is why this screen ends at "propose" and every state
 * transition lives on the run itself.
 *
 * Selection takes open payables due on or before the cut-off **or** still
 * inside their early-settlement discount window — the second arm is the
 * economic point of a payment run. Hand-picking narrows that to an explicit
 * set; leaving the picker empty proposes every eligible open item.
 *
 * NOTE: the contract publishes no list operation for payment runs, so this page
 * cannot show run history. See the backend-gap note in the lane report.
 */
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Checkbox } from "@erp/ui";

import { surfaceProblem } from "@/components/form";
import { MoneyDisplay } from "@/components/money/money-display";
import { useIdempotentMutation } from "@/features/shared/ops-api";
import { useCurrentBranch } from "@/lib/branch/branch-store";

import { proposePaymentRun, type PaymentRun, type PaymentRunCreate } from "../api";
import { todayIsoDate } from "../decimal";
import {
  DateField,
  NoticeBanner,
  PageHeader,
  ProblemBanner,
  TextField,
} from "../components/fields";
import {
  financeKeys,
  usePartyOptions,
  usePaymentRunList,
  useSupplierInvoiceList,
} from "../queries";

export interface PaymentRunsPageProps {
  onProposed: (paymentRunId: string) => void;
  canCreate?: boolean;
  canReadInvoices?: boolean;
  canReadParties?: boolean;
}

export function PaymentRunsPage({
  onProposed,
  canCreate = true,
  canReadInvoices = true,
  canReadParties = true,
}: PaymentRunsPageProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { branchId } = useCurrentBranch();

  const [currency, setCurrency] = useState("SAR");
  const [paymentDate, setPaymentDate] = useState(todayIsoDate);
  const [dueOnOrBefore, setDueOnOrBefore] = useState(todayIsoDate);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [formError, setFormError] = useState<string | null>(null);

  // Posted payables are the ones a run can settle; the server still owns the
  // final eligibility decision, this list is only the hand-picking surface.
  const payablesQuery = useSupplierInvoiceList({ status: "posted" }, 100);
  const runsQuery = usePaymentRunList({}, 20);
  const partiesQuery = usePartyOptions("supplier", canReadParties);

  const partyName = (id: string): string =>
    (partiesQuery.data ?? []).find((party) => party.id === id)?.display_name ?? id;

  const chosenIds = useMemo(
    () =>
      Object.entries(selected)
        .filter(([, on]) => on)
        .map(([id]) => id),
    [selected],
  );

  const proposeMutation = useIdempotentMutation<PaymentRun, PaymentRunCreate>({
    mutationFn: (body, key) => proposePaymentRun(body, key),
    onSuccess: (run) => {
      void queryClient.invalidateQueries({ queryKey: financeKeys.paymentRuns.all });
      onProposed(run.id);
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const submit = () => {
    if (branchId === null) {
      setFormError(
        t(
          "finance.common.branchRequired",
          "Select an acting branch before creating an operational document.",
        ),
      );
      return;
    }
    if (currency.trim().length !== 3) {
      setFormError(
        t("finance.paymentRuns.currencyRequired", "Enter a three-letter currency code."),
      );
      return;
    }
    setFormError(null);
    proposeMutation.mutate({
      branch_id: branchId,
      currency: currency.trim().toUpperCase(),
      payment_date: paymentDate,
      due_on_or_before: dueOnOrBefore,
      ...(chosenIds.length === 0 ? {} : { invoice_ids: chosenIds }),
    });
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <PageHeader
        title={t("finance.paymentRuns.title", "Payment runs")}
        description={t(
          "finance.paymentRuns.subtitle",
          "Propose a run, review it, then submit it for approval.",
        )}
      />

      {branchId === null ? (
        <NoticeBanner>
          {t(
            "finance.common.branchRequired",
            "Select an acting branch before creating an operational document.",
          )}
        </NoticeBanner>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("finance.paymentRuns.proposeSection", "Propose a run")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <TextField
            id="run-currency"
            label={t("finance.common.currency", "Currency")}
            value={currency}
            onChange={(value) => setCurrency(value.toUpperCase())}
            ltr
            maxLength={3}
          />
          <DateField
            id="run-payment-date"
            label={t("finance.paymentRuns.paymentDate", "Payment date")}
            value={paymentDate}
            onChange={setPaymentDate}
          />
          <DateField
            id="run-due-cutoff"
            label={t("finance.paymentRuns.dueOnOrBefore", "Due on or before")}
            value={dueOnOrBefore}
            onChange={setDueOnOrBefore}
            hint={t(
              "finance.paymentRuns.dueCutoffHint",
              "Invoices inside their discount window are selected regardless.",
            )}
          />

          {formError === null ? null : (
            <div className="sm:col-span-3">
              <ProblemBanner>{formError}</ProblemBanner>
            </div>
          )}

          {canCreate ? (
            <div className="sm:col-span-3">
              <Button type="button" loading={proposeMutation.isPending} onClick={submit}>
                {t("finance.paymentRuns.propose", "Propose run")}
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("finance.paymentRuns.pickSection", "Narrow to a hand-picked set")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            {t(
              "finance.paymentRuns.pickHint",
              "Leave everything unticked to propose every eligible open item.",
            )}
          </p>
          {!canReadInvoices ? (
            <p className="text-sm text-muted-foreground">
              {t(
                "finance.paymentRuns.pickForbidden",
                "Hand-picking needs permission to read supplier invoices.",
              )}
            </p>
          ) : payablesQuery.rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("finance.paymentRuns.noPayables", "No posted payables to choose from.")}
            </p>
          ) : (
            <ul className="flex flex-col divide-y">
              {payablesQuery.rows.map((invoice) => (
                <li key={invoice.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                  <Checkbox
                    id={`payable-${invoice.id}`}
                    checked={selected[invoice.id] === true}
                    onCheckedChange={(checked) =>
                      setSelected((current) => ({ ...current, [invoice.id]: checked === true }))
                    }
                  />
                  <label htmlFor={`payable-${invoice.id}`} className="flex-1">
                    <span dir="ltr" className="font-mono">
                      {invoice.document_no}
                    </span>{" "}
                    · {partyName(invoice.party_id)} · <span dir="ltr">{invoice.due_date}</span>
                  </label>
                  <MoneyDisplay value={invoice.grand_total} />
                </li>
              ))}
            </ul>
          )}
          <p className="text-sm text-muted-foreground" data-testid="payment-run-selection-count">
            {t("finance.paymentRuns.selectedCount", "{{count}} invoices hand-picked", {
              count: chosenIds.length,
            })}
          </p>
        </CardContent>
      </Card>

      {/* Run history. Until the list operation existed a run was reachable
          only from the response that proposed it. */}
      <Card>
        <CardHeader>
          <CardTitle>{t("finance.paymentRuns.history", "Recent runs")}</CardTitle>
        </CardHeader>
        <CardContent>
          {runsQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : runsQuery.rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("finance.paymentRuns.noRuns", "No payment run has been proposed yet.")}
            </p>
          ) : (
            <ul className="flex flex-col divide-y">
              {runsQuery.rows.map((run) => (
                <li key={run.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                  <button
                    type="button"
                    onClick={() => onProposed(run.id)}
                    dir="ltr"
                    className="font-mono text-primary underline-offset-4 hover:underline"
                  >
                    {run.run_number}
                  </button>
                  <Badge variant="secondary">{t(`status.${run.status}`, run.status)}</Badge>
                  <span dir="ltr" className="text-muted-foreground">
                    {run.payment_date}
                  </span>
                  <span className="ms-auto">
                    <MoneyDisplay value={run.total_amount} />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
